import React, { useEffect, useState } from 'react';
import { googleLogout, type CredentialResponse } from '@react-oauth/google';
import { GoogleSignInBlock } from '@/components/auth/GoogleSignInBlock';
import { Icon } from '@/components/ui/Icon';
import { LogoImage } from '@/components/ui/LogoImage';
import { Modal } from '@/components/ui/Modal';
import { Colors } from '@/constants/Colors';
import CONFIG from '@/constants/Config';
import { useTheme } from '@/hooks/useTheme';
import { PushNotificationsCard } from '@/components/pwa/PushNotificationsCard';
import { useProfileStore } from '@/stores/profileStore';
import { useClubStore } from '@/stores/clubStore';
import type { Club } from '@/types';

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim();

export default function ProfilePage() {
  const colors = useTheme();
  const {
    pseudo,
    email,
    nom,
    prenom,
    mobile,
    followedClub,
    googleId,
    picture,
    loadProfile,
    saveProfile,
    setFollowedClub,
    signInWithGoogle,
    signOut,
    tifosiCle,
  } = useProfileStore();
  const { fetchClubs, getFilteredClubs, searchQuery, setSearchQuery, isLoading } = useClubStore();

  const [editPseudo, setEditPseudo] = useState(pseudo);
  const [editEmail, setEditEmail] = useState(email);
  const [editNom, setEditNom] = useState(nom ?? '');
  const [editPrenom, setEditPrenom] = useState(prenom ?? '');
  const [editMobile, setEditMobile] = useState(mobile ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [clubPickerVisible, setClubPickerVisible] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const pseudoTrimmed = editPseudo.trim();
  const isPseudoValid = pseudoTrimmed.length > 0;

  useEffect(() => { loadProfile(); }, []);
  useEffect(() => {
    setEditPseudo(pseudo);
    setEditEmail(email);
    setEditNom(nom ?? '');
    setEditPrenom(prenom ?? '');
    setEditMobile(mobile ?? '');
  }, [pseudo, email, nom, prenom, mobile]);

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    setAuthError(null);
    if (!response.credential) {
      setAuthError('Connexion Google interrompue.');
      return;
    }
    try {
      await signInWithGoogle(response.credential);
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Connexion Google impossible.');
    }
  };

  const handleSignOut = async () => {
    try {
      googleLogout();
    } catch {
      // ignore - googleLogout est synchrone et tolerant
    }
    await signOut();
    setAuthError(null);
  };

  const handleSave = async () => {
    if (!isPseudoValid) return;
    setIsSaving(true);
    await saveProfile({
      pseudo: pseudoTrimmed,
      email: editEmail,
      nom: editNom.trim() || undefined,
      prenom: editPrenom.trim() || undefined,
      mobile: editMobile.trim() || undefined,
    });
    setIsSaving(false);
  };

  const handleSelectClub = async (club: Club) => {
    await setFollowedClub(club);
    setClubPickerVisible(false);
  };

  const filteredClubs = getFilteredClubs();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
      <div style={{ padding: '0 16px', paddingBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0, paddingTop: 8 }}>
          <div style={{ width: 40, height: 20 }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>Mon profil</span>
          <div style={{ width: 40 }} />
        </div>
      </div>

      <div className="profile-scroll-area" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, paddingBottom: 40 }}>
        <div style={{ ...styles.card, backgroundColor: colors.surface, borderColor: colors.border, alignItems: 'center', paddingTop: 14, paddingBottom: 14 }}>
          <div style={{ width: 80, height: 80, borderRadius: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${Colors.primary}20`, marginBottom: 12, overflow: 'hidden' }}>
            {picture ? (
              <img src={picture} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
            ) : (
              <Icon name="person" size={40} color={Colors.primary} />
            )}
          </div>
          {googleId ? (
            <>
              {pseudo && (
                <span style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 3 }}>{pseudo}</span>
              )}
              {email && (
                <span style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 12 }}>{email}</span>
              )}
              <button
                onClick={handleSignOut}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  height: 40,
                  borderRadius: 20,
                  paddingLeft: 18,
                  paddingRight: 18,
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.background,
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Se deconnecter
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: colors.textSecondary, marginBottom: 12 }}>CONNEXION</span>
              {GOOGLE_CLIENT_ID ? (
                <GoogleSignInBlock
                  clientConfigured
                  error={authError}
                  onSuccess={handleGoogleSuccess}
                  onError={() => setAuthError('Connexion Google echouee.')}
                  text="signin_with"
                />
              ) : (
                <span style={{ fontSize: 12, color: colors.textSecondary, textAlign: 'center', maxWidth: 260 }}>
                  Connexion Google indisponible : VITE_GOOGLE_CLIENT_ID non configure.
                </span>
              )}
            </>
          )}
        </div>

        <div style={{ ...styles.card, backgroundColor: colors.surface, borderColor: colors.border }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: colors.textSecondary }}>CLUB SUIVI</span>
          <button
            onClick={() => { fetchClubs(); setClubPickerVisible(true); }}
            style={{ ...styles.clubSelector, backgroundColor: colors.background, borderColor: colors.border }}
          >
            {followedClub ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <LogoImage uri={followedClub.logo} size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>{followedClub.nom}</div>
                  {followedClub.ville && <div style={{ fontSize: 12, marginTop: 2, color: colors.textSecondary }}>{followedClub.ville}</div>}
                </div>
                <Icon name="chevron-down" size={20} color={colors.textSecondary} />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Icon name="add-circle-outline" size={24} color={Colors.primary} />
                <span style={{ fontSize: 14, flex: 1, color: colors.textSecondary }}>Choisir un club à suivre</span>
                <Icon name="chevron-forward" size={20} color={colors.textSecondary} />
              </div>
            )}
          </button>
          <label style={{ ...styles.label, color: colors.textSecondary }}>Pseudo</label>
          <input
            style={{ ...styles.input, backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
            value={editPseudo}
            onChange={(e) => setEditPseudo(e.target.value)}
            placeholder="Votre pseudo"
            required
          />
          {!isPseudoValid && (
            <span style={{ fontSize: 12, color: '#ff6b6b', marginTop: 6 }}>
              Le pseudo est obligatoire.
            </span>
          )}
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ ...styles.label, color: colors.textSecondary }}>Prénom</label>
              <input
                style={{ ...styles.input, backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
                value={editPrenom}
                onChange={(e) => setEditPrenom(e.target.value)}
                placeholder="Prénom"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ ...styles.label, color: colors.textSecondary }}>Nom</label>
              <input
                style={{ ...styles.input, backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
                value={editNom}
                onChange={(e) => setEditNom(e.target.value)}
                placeholder="Nom"
              />
            </div>
          </div>
          <label style={{ ...styles.label, color: colors.textSecondary, marginTop: 16 }}>Email</label>
          <input
            type="email"
            style={{ ...styles.input, backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            placeholder="votre@email.com"
          />
          <label style={{ ...styles.label, color: colors.textSecondary, marginTop: 16 }}>Mobile</label>
          <input
            type="tel"
            style={{ ...styles.input, backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
            value={editMobile}
            onChange={(e) => setEditMobile(e.target.value)}
            placeholder="06 12 34 56 78"
          />
          <button onClick={handleSave} disabled={isSaving || !isPseudoValid} style={{ ...styles.saveButton, backgroundColor: Colors.primary, opacity: isSaving || !isPseudoValid ? 0.6 : 1 }}>
            {isSaving ? <div className="spinner spinner-small" style={{ borderTopColor: '#fff' }} /> : (
              <>
                <Icon name="checkmark" size={20} color="#ffffff" />
                <span style={{ color: '#ffffff', fontSize: 16, fontWeight: 700 }}>Enregistrer</span>
              </>
            )}
          </button>
        </div>

        <PushNotificationsCard tifosiCle={tifosiCle} />

        <div style={{ textAlign: 'center', fontSize: 11, color: colors.textSecondary }}>
          Version {CONFIG.APP_VERSION}
        </div>
      </div>

      <Modal visible={clubPickerVisible} onClose={() => setClubPickerVisible(false)} variant="auto">
        <div style={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: `1px solid ${colors.border}` }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>Choisir un club</span>
            <button onClick={() => setClubPickerVisible(false)} style={{ cursor: 'pointer' }}><Icon name="close" size={28} color={colors.text} /></button>
          </div>
          <div style={{ padding: 16 }}>
            <input
              style={{ ...styles.searchInput, backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }}
              placeholder="Rechercher un club..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {isLoading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="spinner" /></div>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
              {filteredClubs.map((item) => (
                <button key={item.idclub} onClick={() => handleSelectClub(item)} style={{ ...styles.clubItem, backgroundColor: colors.surface, borderColor: colors.border }}>
                  <LogoImage uri={item.logo} size={40} />
                  <div style={{ flex: 1, marginLeft: 12 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>{item.nom}</div>
                    {item.ville && <div style={{ fontSize: 13, marginTop: 2, color: colors.textSecondary }}>{item.ville}</div>}
                  </div>
                  {followedClub?.idclub === item.idclub && <Icon name="checkmark-circle" size={24} color={Colors.accent.green} />}
                </button>
              ))}
            </div>
          )}
          <div style={{ padding: '16px 16px 32px', borderTop: `1px solid ${colors.border}` }}>
            <button onClick={() => { setFollowedClub(null); setClubPickerVisible(false); }} style={{ ...styles.clearButton, borderColor: colors.border }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>Aucun club</span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: { padding: 16, borderRadius: 16, border: '1px solid', marginBottom: 16, display: 'flex', flexDirection: 'column' },
  label: { fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' },
  input: { height: 48, borderRadius: 12, border: '1px solid', paddingLeft: 14, paddingRight: 14, fontSize: 16, outline: 'none', width: '100%' },
  saveButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 48, borderRadius: 24, marginTop: 20, cursor: 'pointer', width: '100%' },
  clubSelector: { borderRadius: 12, border: '1px solid', padding: 12, marginTop: 12, cursor: 'pointer', width: '100%', textAlign: 'left' },
  searchInput: { height: 44, borderRadius: 12, border: '1px solid', paddingLeft: 14, paddingRight: 14, fontSize: 16, outline: 'none', width: '100%' },
  clubItem: { display: 'flex', alignItems: 'center', padding: 12, borderRadius: 12, border: '1px solid', marginBottom: 8, cursor: 'pointer', width: '100%', textAlign: 'left' },
  clearButton: { padding: 14, borderRadius: 12, border: '1px solid', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
};
